import React, { Component } from "react";
import {
    MDBModal,
    MDBModalBody,
    MDBModalHeader,
    MDBModalFooter,
    MDBBtn,
    MDBIcon,
    MDBRow,
    MDBCol,
} from "mdbreact";
import "./MultiTenancy.css";

/**
 * Approval Detail Modal — Mockup #3
 *
 * Shows full request details and requires the user to check the explicit
 * billing/access acknowledgment checkbox before the Approve button is enabled.
 *
 * Props:
 *   isOpen, onClose, onApprove(requestId), onReject(requestId), request
 */
class ApprovalDetailModal extends Component {
    constructor(props) {
        super(props);
        this.state = {
            acknowledged: false,
            submitting: false,
        };
    }

    componentDidUpdate(prevProps) {
        // Reset when the modal closes
        if (prevProps.isOpen && !this.props.isOpen) {
            this.setState({ acknowledged: false, submitting: false });
        }
    }

    toggleAcknowledgment = () => {
        this.setState({ acknowledged: !this.state.acknowledged });
    };

    handleApprove = () => {
        const { request, onApprove } = this.props;
        if (!this.state.acknowledged || !request) return;
        this.setState({ submitting: true });
        if (onApprove) onApprove(request.id);
    };

    handleReject = () => {
        const { request, onReject } = this.props;
        if (!request) return;
        this.setState({ submitting: true });
        if (onReject) onReject(request.id);
    };

    renderPermissionCheck = (label, allowed) => {
        return (
            <div className="mt-permission-row">
                <span
                    className={
                        allowed
                            ? "mt-perm-allowed"
                            : "mt-perm-denied"
                    }
                >
                    {allowed ? (
                        <MDBIcon icon="check" />
                    ) : (
                        <MDBIcon icon="times" />
                    )}
                </span>
                <span
                    className={
                        allowed
                            ? "mt-perm-label"
                            : "mt-perm-label mt-perm-label-denied"
                    }
                >
                    {label}
                </span>
            </div>
        );
    };

    renderPermissionList = (perms) => {
        if (!perms) return null;
        return (
            <MDBRow>
                <MDBCol md="6">
                    {this.renderPermissionCheck("View Venue", perms.viewVenue)}
                    {this.renderPermissionCheck(
                        "View Equipment Status",
                        perms.viewEquipment
                    )}
                    {this.renderPermissionCheck(
                        "View Users",
                        perms.viewUsers
                    )}
                    {this.renderPermissionCheck(
                        "Create Users",
                        perms.createUsers
                    )}
                    {this.renderPermissionCheck("Edit Users", perms.editUsers)}
                    {this.renderPermissionCheck(
                        "Delete Users",
                        perms.deleteUsers
                    )}
                    {this.renderPermissionCheck(
                        "Edit Venue Settings",
                        perms.editVenueSettings
                    )}
                    {this.renderPermissionCheck(
                        "Manage Admins",
                        perms.manageAdmins
                    )}
                </MDBCol>
                <MDBCol md="6">
                    {this.renderPermissionCheck(
                        "View Subscriptions",
                        perms.viewSubscriptions
                    )}
                    {this.renderPermissionCheck(
                        "Create Subscriptions",
                        perms.createSubscriptions
                    )}
                    {this.renderPermissionCheck(
                        "Edit Subscriptions",
                        perms.editSubscriptions
                    )}
                    {this.renderPermissionCheck(
                        "Delete Subscriptions",
                        perms.deleteSubscriptions
                    )}
                    {this.renderPermissionCheck(
                        "Create Resident Tickets",
                        perms.createResidentTickets
                    )}
                    {this.renderPermissionCheck(
                        "View Resident Tickets",
                        perms.viewResidentTickets
                    )}
                    {this.renderPermissionCheck(
                        "View Resident Ticket Details (PII)",
                        perms.viewResidentTicketDetails
                    )}
                    {this.renderPermissionCheck(
                        "Property Tickets",
                        perms.createPropertyTickets ||
                            perms.viewPropertyTickets
                    )}
                </MDBCol>
            </MDBRow>
        );
    };

    render() {
        const { isOpen, onClose, request } = this.props;
        const { acknowledged, submitting } = this.state;

        if (!request) {
            return null;
        }

        return (
            <MDBModal isOpen={isOpen} toggle={onClose} size="lg">
                <MDBModalHeader toggle={onClose}>
                    <h4>Review Association Request</h4>
                </MDBModalHeader>
                <MDBModalBody>
                    <div className="mt-approval-section">
                        <h6 className="mt-approval-section-title">
                            Request Details
                        </h6>
                        <MDBRow>
                            <MDBCol md="6">
                                <p>
                                    <strong>Primary Operator: </strong>
                                    {request.primaryOperatorName}
                                </p>
                                <p>
                                    <strong>Venue: </strong>
                                    {request.venueName}
                                </p>
                            </MDBCol>
                            <MDBCol md="6">
                                <p>
                                    <strong>Address: </strong>
                                    {request.venueAddress}
                                </p>
                                <p>
                                    <strong>Requested: </strong>
                                    {request.requestedAt}
                                </p>
                                <p>
                                    <strong>Requested By: </strong>
                                    {request.requestedByEmail}
                                </p>
                            </MDBCol>
                        </MDBRow>
                    </div>

                    <div className="mt-approval-section">
                        <h6 className="mt-approval-section-title">
                            Your Permissions
                            <span className="mt-approval-subtitle">
                                {" "}
                                (as configured by Primary Operator)
                            </span>
                        </h6>
                        {this.renderPermissionList(request.permissions)}
                    </div>

                    <div className="mt-approval-section mt-approval-acknowledgment">
                        <h6 className="mt-approval-section-title">
                            <MDBIcon icon="exclamation-triangle" /> Billing /
                            Access Acknowledgment
                        </h6>
                        <div className="mt-acknowledgment-box">
                            <label className="mt-checkbox-label">
                                <input
                                    type="checkbox"
                                    checked={acknowledged}
                                    onChange={this.toggleAcknowledgment}
                                />
                                <span>
                                    I acknowledge that approving this
                                    association will grant my operator access to
                                    this venue and that this venue may be
                                    included in my VAULT access or billing count
                                    according to my agreement with RoamingiQ.
                                </span>
                            </label>
                        </div>
                    </div>
                </MDBModalBody>
                <MDBModalFooter>
                    <MDBBtn
                        color="warning"
                        onClick={this.handleReject}
                        disabled={submitting}
                    >
                        <MDBIcon icon="times" /> Reject
                    </MDBBtn>
                    <MDBBtn
                        color="success"
                        onClick={this.handleApprove}
                        disabled={!acknowledged || submitting}
                    >
                        <MDBIcon icon="check" /> Approve Association
                    </MDBBtn>
                </MDBModalFooter>
            </MDBModal>
        );
    }
}

export default ApprovalDetailModal;
